import type { CustomFieldInputType, FieldColumn, PersonRow } from "@/api/db/connection";
import { childrenOf } from "@/api/document/block";
import type { DocumentBlock, TreeNode } from "@/api/document/block";
import { CustomFieldValues } from "@/api/document/custom-field-values";
import { djs } from "@/lib/djs";

type FieldType = "string" | "number" | "date";

type AutoColumn = Exclude<FieldColumn, "custom">;

const fieldTypes: Record<AutoColumn, FieldType> = {
	name: "string",
	cpf: "string",
	rg: "string",
	mother_name: "string",
	occupation: "string",
	marital_status: "string",
	address: "string",
	phone: "string",
	email: "string",
	age: "number",
	birth_date: "date",
	current_date: "date",
};

type PersonColumn = Exclude<AutoColumn, "current_date" | "age">;
type CivilColumn = Exclude<PersonColumn, "address" | "phone" | "email">;

export interface FieldContext {
	person: Pick<PersonRow, CivilColumn> & {
		address: string | null;
		phone: string | null;
		email: string | null;
	};
	date: Date;
	custom: Map<number, string>;
}

interface PhoneSource {
	norm: string | null;
	kind: string;
	country_code: string | null;
}

interface EmailSource {
	email: string;
	invalid: boolean;
}

interface AddressSource {
	raw: string | null;
	cep: string | null;
	street: string | null;
	number: string | null;
	complement: string | null;
	district: string | null;
	city: string | null;
	state: string | null;
}

export interface PersonFields extends Pick<PersonRow, CivilColumn> {
	phones: PhoneSource[];
	emails: EmailSource[];
	addresses: AddressSource[];
}

function applyMask(value: string, mask: string) {
	const digits = value.replaceAll(/\D/gu, "");
	const slots = mask.split("").filter((char) => char === "#").length;

	if (digits.length !== slots) {
		return null;
	}

	let cursor = 0;

	return mask
		.split("")
		.map((char) => (char === "#" ? digits[cursor++] : char))
		.join("");
}

function resolveString(value: string, format: string | null) {
	const trimmed = value.trim();

	if (!trimmed) {
		return null;
	}

	if (format === null) {
		return trimmed;
	}

	return applyMask(trimmed, format);
}

function formatCep(cep: string) {
	const digits = cep.replaceAll(/\D/gu, "");

	return digits.length === 8 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : cep.trim();
}

function isSignableMobile(phone: PhoneSource) {
	if (phone.kind !== "mobile") {
		return false;
	}

	if (phone.country_code !== null && phone.country_code !== "55") {
		return false;
	}

	if (phone.norm === null) {
		return false;
	}

	return phone.norm.length >= 10 && phone.norm.length <= 11;
}

function formatBrazilPhone(norm: string) {
	const digits = norm.replaceAll(/\D/gu, "");

	if (digits.length === 11) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
	}

	if (digits.length === 10) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
	}

	return norm;
}

function pickPhone(phones: PhoneSource[]) {
	const phone = phones.find(isSignableMobile);

	return phone === undefined || phone.norm === null ? null : formatBrazilPhone(phone.norm);
}

function pickEmail(emails: EmailSource[]) {
	const valid = emails.find((email) => !email.invalid);

	return valid === undefined ? null : valid.email;
}

function formatAddress(address: AddressSource | undefined) {
	if (address === undefined) {
		return null;
	}

	const parts: string[] = [];
	const street = address.street?.trim();

	if (street) {
		const number = address.number?.trim();
		parts.push(number ? `${street}, ${number}` : street);
	}

	const complement = address.complement?.trim();
	if (complement) {
		parts.push(complement);
	}

	const district = address.district?.trim();
	if (district) {
		parts.push(district);
	}

	const city = address.city?.trim();
	const state = address.state?.trim();
	if (city && state) {
		parts.push(`${city}/${state}`);
	} else if (city) {
		parts.push(city);
	} else if (state) {
		parts.push(state);
	}

	const cep = address.cep?.trim();
	if (cep) {
		parts.push(`CEP ${formatCep(cep)}`);
	}

	if (parts.length > 0) {
		return parts.join(", ");
	}

	const raw = address.raw?.trim();

	return raw ? raw : null;
}

function ageInYears(birthDate: Date | null, at: Date) {
	if (birthDate === null) {
		return null;
	}

	return djs(at).diff(djs(birthDate), "year");
}

function columnValue(column: AutoColumn, context: FieldContext): string | number | Date | null {
	if (column === "current_date") {
		return context.date;
	}

	if (column === "age") {
		return ageInYears(context.person.birth_date, context.date);
	}

	return context.person[column];
}

function collectFieldIds(node: TreeNode, ids: Set<number>) {
	const children = childrenOf(node);

	if (children !== null) {
		for (const child of children) {
			collectFieldIds(child, ids);
		}

		return;
	}

	if (node.type === "page-break" || node.type === "image") {
		return;
	}

	for (const inline of node.content ?? []) {
		if (inline.type === "field") {
			ids.add(inline.attrs.fieldId);
		}
	}
}

const ABSENT_PERSON: FieldContext["person"] = {
	name: "",
	cpf: null,
	rg: null,
	birth_date: null,
	mother_name: null,
	occupation: null,
	marital_status: null,
	address: null,
	phone: null,
	email: null,
};

export const DocumentFields = {
	context(
		person: FieldContext["person"] | null,
		date: Date,
		custom: Map<number, string> = new Map(),
	): FieldContext {
		return { person: person ?? ABSENT_PERSON, date, custom };
	},

	person(source: PersonFields | null): FieldContext["person"] {
		if (source === null) {
			return ABSENT_PERSON;
		}

		return {
			name: source.name,
			cpf: source.cpf,
			rg: source.rg,
			birth_date: source.birth_date,
			mother_name: source.mother_name,
			occupation: source.occupation,
			marital_status: source.marital_status,
			address: formatAddress(source.addresses[0]),
			phone: pickPhone(source.phones),
			email: pickEmail(source.emails),
		};
	},

	typeOf(column: AutoColumn) {
		return fieldTypes[column];
	},

	referenced(blocks: DocumentBlock[]) {
		const ids = new Set<number>();

		for (const block of blocks) {
			collectFieldIds(block, ids);
		}

		return ids;
	},

	resolve(
		field: {
			id: number;
			column: FieldColumn;
			format: string | null;
			input_type: CustomFieldInputType;
		},
		context: FieldContext,
	) {
		if (field.column === "custom") {
			const value = context.custom.get(field.id);

			if (value === undefined) {
				return null;
			}

			return CustomFieldValues.format(field.input_type, value);
		}

		const value = columnValue(field.column, context);

		if (value === null) {
			return null;
		}

		switch (fieldTypes[field.column]) {
			case "string":
				return resolveString(String(value), field.format);
			case "number":
				return String(value);
			case "date":
				return djs(value)
					.locale("pt-br")
					.format(field.format ?? "DD/MM/YYYY");
		}
	},
};
